import Styled from '@/components/react-hook-form-inputs/checkbox/checkbox-style';
import { getNestedErrorMessage } from '@/utils/utils';
import propTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';

/**
 * Register options for React Hook Form.
 *
 * @typedef {object} RegisterOptions
 * @property {React.Ref<any>} [ref] - React element ref.
 * @property {boolean | { value: boolean, message: string }} [required] - A Boolean which, if true, indicates that the input must have a value before the form can be submitted. You can assign a string to return an error message in the errors object. Note: This config aligns with web constrained API for required input validation, for object or array type of input use validate function instead.
 * @property {{ value: number, message: string }} [maxLength] - The maximum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [minLength] - The minimum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [max] - The maximum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [min] - The minimum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: RegExp, message: string }} [pattern] - A regular expression to match the input value, and an optional `message` (string) for the error message.
 * @property {(value: any) => boolean | string | Promise<boolean | string>} [validate] - A custom validation function or a function returning a promise resolving to a boolean or string. If a string is returned, it will be used as the error message.
 * @property {boolean} [valueAsNumber] - Whether to treat the value as a number (useful for numeric inputs).
 * @property {boolean} [valueAsDate] - Whether to treat the value as a date.
 * @property {(value: any) => any} [setValueAs] - A function to transform the value before setting it in the form state.
 * @property {boolean} [disabled] - Whether the input is disabled (default: false).
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - A function to handle input changes.
 * @property {(event: React.FocusEvent<HTMLInputElement>) => void} [onBlur] - A function to handle input blur events.
 * @property {any} [value] - The initial value of the input.
 * @property {boolean} [shouldUnregister] - Whether to unregister the input on unmount.
 * @property {string | string[]} [deps] - The names of other fields that affect validation of this input.
 */

/**
 * This is a Checkbox component that can be used throughout ETTX.
 * @param {object} props - The props of the component.
 * @param {string} props.name - The name of the input for React Hook Form (required).
 * @param {boolean} props.disabled - The disabled state of the toggle.
 * @param {bool} props.defaultValue - The default value of the component.
 * @param {bool} props.checked - The checked value of the component.
 * @param {object} props.label - The label element of the component.
 * @param {string} props.margin - The margin values of the component.
 * @param {string} props.cy - The name of the input for Cypress tests.
 * @param {RegisterOptions} props.registerOptions - The register options for React Hook Form.
 * @param {object} props.style - The style element of the component
 * @param {object} props.labelStyle - The label style element of the component
 * @param {string} props.width - The width element of the component
 * @param {func} props.onClick - The onClick handler of the component
 * @param {bool} props.indeterminate - The indeterminate state of the component. When set to true, the checkmark is just a horizontal line.
 * @returns {JSX.Element} The Toggle component.
 */
const Checkbox = ({
  style,
  labelStyle,
  width,
  margin,
  label,
  name,
  registerOptions,
  defaultValue,
  cy,
  onClick,
  disabled,
  checked,
  indeterminate,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = getNestedErrorMessage(name, errors);

  return (
    <>
      <Styled.Container
        style={style}
        width={width}
        margin={margin}
        data-cy={cy}
      >
        <Controller
          control={control}
          name={name}
          key={name}
          defaultValue={defaultValue}
          rules={registerOptions}
          render={({ field }) => (
            <Styled.CheckboxContainer>
              <Styled.LabelSpan style={labelStyle}>{label}</Styled.LabelSpan>
              <Styled.CheckBox
                type="checkbox"
                id={`${name}-checkbox`}
                onClick={onClick}
                onChange={field.onChange}
                checked={checked ?? field.value}
                disabled={disabled}
                readOnly={disabled}
              />
              <Styled.CheckMark
                className="checkMark"
                $indeterminate={indeterminate}
              />
            </Styled.CheckboxContainer>
          )}
        />
      </Styled.Container>
      {errorMessage && (
        <Styled.ErrorMessage>{errorMessage}</Styled.ErrorMessage>
      )}
    </>
  );
};

Checkbox.defaultProps = {
  style: undefined,
  labelStyle: undefined,
  name: '',
  width: '',
  margin: '',
  defaultValue: undefined,
  checked: undefined,
  disabled: undefined,
  registerOptions: {
    onChange: () => {},
  },
  error: undefined,
  cy: undefined,
  onClick: undefined,
  indeterminate: false,
};

Checkbox.propTypes = {
  style: propTypes.shape({}),
  labelStyle: propTypes.shape({}),
  width: propTypes.string,
  defaultValue: propTypes.bool,
  checked: propTypes.bool,
  disabled: propTypes.bool,
  margin: propTypes.string,
  label: propTypes.oneOfType([propTypes.string, propTypes.object]).isRequired,
  registerOptions: propTypes.shape({}),
  name: propTypes.string,
  error: propTypes.shape({}),
  cy: propTypes.string,
  onClick: propTypes.func,
  indeterminate: propTypes.bool,
};

export default Checkbox;
